import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Typography } from '@mui/material';
import { type GridColDef, type GridRenderEditCellParams } from '@mui/x-data-grid-premium';

import { AuthorizerContext } from 'app/providers/acl/AclProvider';
import { Utils, useIsModuleDisabled, useSettings } from 'app/main/components';
import type { MiddlewhereFilter } from 'app/main/components/filter/FilterItemManager';
import type { DetailsSectionInfoContentProps, ViewFooterCustomInfoProps } from 'app/main/components/page-layouts';
import DetailsSectionTable from 'app/main/components/page-layouts/details/sections/table/DetailsSectionTable';
import DetailsSectionTableInputCells from 'app/main/components/page-layouts/details/sections/table/DetailsSectionTableInputCells';
import { renderCellWithTooltip, renderCurrency } from 'app/main/components/page-layouts/view/table/ViewTableUtils';

type InvoicesDetailsSectionRowsProps = DetailsSectionInfoContentProps & {
	newRowBaseModel?: Record<string, any>;
};

const TableTypeEditInputCell = (params: GridRenderEditCellParams & { options: string[] }) => {
	const { t } = useSettings();

	return (
		<DetailsSectionTableInputCells.SelectEditInputCell
			{...params}
			options={params.options.map(table => ({
				value: table,
				title: t(`MODULE_${table}_NAME_SINGULAR`)
			}))}
			parseValue={newValue => ({
				inrw_table: newValue.value,
				inrw_table_uid: null,
				inrw_table_title: null,
				inrw_table_line_uid: null,
				inrw_table_line_title: null,
				linked_comp_uid: null,
				linked_comp_title: null
			})}
		/>
	);
};

const TableEditInputCell = (params: GridRenderEditCellParams) => {
	const { settings } = useSettings(params.row.inrw_table);

	const getOptionLabel = option => {
		const title = option[`${settings.modu_short}_title`];
		return title ? `${title}${option.comp_title ? ` - ${option.comp_title}` : ''}` : '';
	};

	return settings ? (
		<DetailsSectionTableInputCells.ModuleSearchEditInputCell
			{...params}
			module={params.row.inrw_table}
			value={params.row.inrw_table_uid}
			getOptionLabel={getOptionLabel}
			parseValue={newValue => ({
				inrw_table_uid: newValue[`${settings.modu_short}_uid`],
				inrw_table_title: newValue[`${settings.modu_short}_title`],
				inrw_table_line_uid: null,
				inrw_table_line_title: null,
				linked_comp_uid: newValue.comp_uid,
				linked_comp_title: newValue.comp_title
			})}
		/>
	) : (
		<></>
	);
};

const TableLineEditInputCell = (params: GridRenderEditCellParams) => {
	const module = ['contracts', 'orders'].includes(params.row.inrw_table)
		? `${params.row.inrw_table}_lines`
		: undefined;

	const { settings } = useSettings(module);
	const { settings: parentSettings } = useSettings(params.row.inrw_table);

	const defaultFilters: MiddlewhereFilter[] =
		module === 'orders_lines' ? [{ filt_field: 'orli_type', filt_clause: 'not_equal', filt_value: 'note' }] : [];

	const getOptionLabel = option => {
		if (module === 'contracts_lines') return option.cnli_title;
		if (module === 'orders_lines') return option.orli_title;
	};

	return settings && parentSettings ? (
		<DetailsSectionTableInputCells.ModuleSearchEditInputCell
			{...params}
			module={settings.modu_title}
			filters={[
				...defaultFilters,
				{
					filt_field: `${settings.modu_short}_${parentSettings.modu_short}_uid`,
					filt_clause: 'equals',
					filt_value: params.row.inrw_table_uid
				}
			]}
			value={params.row.inrw_table_line_uid}
			getOptionLabel={getOptionLabel}
			parseValue={newValue => ({
				inrw_table_line_uid: newValue[`${settings.modu_short}_uid`],
				inrw_table_line_title: getOptionLabel(newValue),
				cnli_d_billing: newValue.cnli_d_billing,
				orli_order: newValue.orli_order
			})}
		/>
	) : (
		<></>
	);
};

export default function InvoicesDetailsSectionRows(props: InvoicesDetailsSectionRowsProps) {
	const { sysSettings, t } = useSettings();
	const isModuleDisabled = useIsModuleDisabled();
	const authorizer = React.useContext(AuthorizerContext);

	const tableOptions = ['contracts', 'orders'];
	if (sysSettings.projects_show_financial_stats === 'true') tableOptions.push('projects');
	const availableTableOptions = tableOptions.filter(
		table => !isModuleDisabled(table) && authorizer.can('update', table)
	);

	const columns: GridColDef[] = [];

	if (availableTableOptions.length >= 0) {
		if (availableTableOptions.length >= 1) {
			columns.push({
				field: 'inrw_table',
				headerName: t('INVOICES_ROWS_COLUMN_TABLE'),
				minWidth: 100,
				renderCell: ({ value }) =>
					renderCellWithTooltip({ value: value ? t(`MODULE_${value}_NAME_SINGULAR`) : '' }),
				editable: true,
				renderEditCell: params => <TableTypeEditInputCell options={availableTableOptions} {...params} />
			});
		}

		columns.push({
			field: 'inrw_table_title',
			headerName: t('INVOICES_ROWS_COLUMN_TABLE_TITLE'),
			minWidth: 200,
			flex: 1,
			renderCell: ({ value, row }) =>
				renderCellWithTooltip({
					value: `${value || ''}${row.linked_comp_title ? ` - ${row.linked_comp_title}` : ''}`,
					to: { module: row.inrw_table, uid: row.inrw_table_uid }
				}),
			editable: true,
			renderEditCell: params => <TableEditInputCell {...params} />
		});

		if (sysSettings.invoices_rows_link_lines_enabled === 'true') {
			columns.push({
				field: 'inrw_table_line_title',
				headerName: t('INVOICES_ROWS_COLUMN_TABLE_LINE_TITLE'),
				minWidth: 200,
				flex: 1,
				renderCell: renderCellWithTooltip,
				editable: true,
				renderEditCell: params => <TableLineEditInputCell {...params} />
			});
		}
	}

	columns.push(
		{
			field: 'inrw_description',
			headerName: t('INVOICES_ROWS_COLUMN_DESCRIPTION'),
			minWidth: 200,
			flex: 1,
			renderCell: renderCellWithTooltip,
			editable: true,
			renderEditCell: params => <DetailsSectionTableInputCells.TextEditInputCell {...params} rows={4} />
		},
		{
			field: 'inrw_amount',
			type: 'number',
			headerName: t('INVOICES_ROWS_COLUMN_AMOUNT'),
			minWidth: 150,
			renderCell: renderCurrency,
			editable: true,
			renderEditCell: params => <DetailsSectionTableInputCells.CurrencyEditInputCell {...params} />
		}
	);

	const FooterInfo = (footerProps: ViewFooterCustomInfoProps) => {
		const totRowsAmount = Utils.calcObjectsTotal(footerProps.items || [], 'inrw_amount');
		const totInvoiceAmountResidual = (props.model.invc_amount_tax_excl || 0) - totRowsAmount;

		return (
			<div className="h-full p-4">
				<Typography variant="subtitle2">
					<FormattedMessage
						id="invoicesSectionRows.totRowsAmount"
						defaultMessage={t('INVOICES_ROWS_SECTION_TOT_ROWS_AMOUNT')}
						values={{ value: Utils.formatCurrency(totRowsAmount) }}
					/>
				</Typography>
				<Typography variant="subtitle2">
					<FormattedMessage
						id="invoicesSectionRows.totInvoiceAmountResidual"
						defaultMessage={t('INVOICES_ROWS_SECTION_TOT_INVOICE_AMOUNT_RESIDUAL')}
						values={{ value: Utils.formatCurrency(totInvoiceAmountResidual) }}
					/>
				</Typography>
			</div>
		);
	};

	return (
		<DetailsSectionTable
			name="invc_rows"
			columns={columns}
			idField="inrw_uid"
			rows={props.model.invc_rows || []}
			slots={{
				footerCustomInfo: FooterInfo
			}}
			isEdit={props.isEdit}
			sendChange={props.sendChange}
			editProps={{
				editMode: 'row',
				newRowBaseModel: props.newRowBaseModel
			}}
			disabledAdd={!props.model.invc_uid}
		/>
	);
}
